import React, { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isLoggedIn = () => {
  try {
    return !!JSON.parse(localStorage.getItem("ranker-auth") || "{}").token;
  } catch {
    return false;
  }
};

/** Global gate: after login, blocks the app until the user provides a mobile number. */
export default function PhoneGate() {
  const [needed, setNeeded] = useState(false);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    const check = () => {
      if (!isLoggedIn()) { setNeeded(false); return; }
      axios
        .get(`${API}/auth/phone-status`)
        .then((r) => { if (alive) setNeeded(!r.data.has_phone); })
        .catch(() => {});
    };
    check();
    const onNav = () => check();
    window.addEventListener("focus", onNav);
    return () => { alive = false; window.removeEventListener("focus", onNav); };
  }, []);

  const submit = async () => {
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length < 10) { setErr("Please enter a valid 10-digit mobile number"); return; }
    setSaving(true); setErr("");
    try {
      await axios.post(`${API}/auth/phone`, { phone });
      setNeeded(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!needed) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-stone-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="phone-gate">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
        <div className="h-12 w-12 rounded-xl bg-orange-50 grid place-items-center mb-4">
          <Phone className="w-6 h-6 text-orange-500" />
        </div>
        <h2 className="text-xl font-bold text-stone-900 font-heading">Add your mobile number</h2>
        <p className="text-sm text-stone-500 mt-1">
          We need your mobile number to keep your account secure and send important exam updates.
        </p>
        <div className="mt-4 flex items-center rounded-xl border border-stone-200 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/20 overflow-hidden">
          <span className="px-3 text-sm text-stone-500 bg-stone-50 border-r border-stone-200 py-3">+91</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            inputMode="tel"
            placeholder="10-digit mobile number"
            data-testid="phone-gate-input"
            className="flex-1 px-3 py-3 text-sm outline-none text-stone-900"
          />
        </div>
        {err && <p className="text-xs text-rose-600 mt-2" data-testid="phone-gate-error">{err}</p>}
        <button
          onClick={submit}
          disabled={saving}
          data-testid="phone-gate-submit"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm py-3 disabled:opacity-60 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save & Continue
        </button>
      </div>
    </div>
  );
}
